import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";
import { isToday, isFuture, startOfDay } from "date-fns";

type Stream = Database['public']['Tables']['streams']['Row'];

export type StreamWithProfile = Stream & {
  profiles: {
    username: string | null;
  } | null;
};

export type StreamSection = "live" | "upcoming" | "past";

/**
 * Classifies a stream into live/upcoming/past based on scheduled_time date vs today.
 * Streams with is_live=true always classify as live regardless of date.
 */
export function classifyStream(stream: StreamWithProfile): StreamSection {
  if (stream.is_live) return "live";
  if (!stream.scheduled_time) return "past";
  const date = startOfDay(new Date(stream.scheduled_time));
  if (isToday(date)) return "live";
  if (isFuture(date)) return "upcoming";
  return "past";
}

/**
 * Fetches streams and enriches them with profile data
 */
export const fetchStreamsWithProfiles = async (
  query: PromiseLike<{ data: Stream[] | null; error: unknown }>
): Promise<StreamWithProfile[]> => {
  const { data: streams, error } = await query;

  if (error || !streams) {
    console.error("Error fetching streams:", error);
    return [];
  }

  // Get unique streamer IDs
  const allStreamerIds = streams
    .map((s) => s.streamer_id)
    .filter((id): id is string => typeof id === 'string' && id !== null);

  const streamerIds = [...new Set(allStreamerIds)];

  // Fetch profiles for all streamers if there are any
  if (streamerIds.length === 0) {
    return streams.map((stream) => ({
      ...stream,
      profiles: null,
    }));
  }

  const { data: profiles } = await supabase
    .from("profiles")
    .select("id, username")
    .in("id", streamerIds as string[]);

  // Create a map of profiles
  const profileMap = new Map(
    profiles?.map((p) => [p.id, { username: p.username }]) || []
  );

  // Enrich streams with profile data
  return streams.map((stream) => ({
    ...stream,
    profiles: stream.streamer_id ? profileMap.get(stream.streamer_id) || null : null,
  }));
};
